import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import AthleteSearch from "./AthleteSearch";
import AthleteChip from "./AthleteChip";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CircularProgressBar from "../../../components/CircularProgressBar";
import { useAxiosInstance } from "../../../services/Interceptor";
import { SearchAthletesPagedAsync } from "../../../services/AthleteService";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

const PAGE_SIZE = 20;

function mergeAthleteProfileIntoListItem(athlete, profile) {
  if (!athlete || !profile) return athlete;
  return {
    ...athlete,
    ...profile,
    name: profile.firstName ?? profile.name ?? athlete.name,
    lastname: profile.lastName ?? profile.lastname ?? athlete.lastname,
    position: profile.primaryPosition ?? profile.position ?? athlete.position,
    primaryPosition: profile.primaryPosition ?? athlete.primaryPosition,
    expectedYearOfGraduation:
      profile.expectedYearOfGraduation ?? athlete.expectedYearOfGraduation,
    highSchool: profile.highSchool ?? athlete.highSchool,
  };
}

export default function AthleteSearchPanel({
  authUser,
  selectedAthlete,
  onAthleteSelected,
  adjustedHeight,
  highSchool,
  listVariant = "rows", // "rows" (admin Athletes page) | "chips" (coach dashboard, admin dashboard)
  chipProps = {},
  favoritesOnly = false,
  refreshKey = 0,
}) {
  const apiInstance = useAxiosInstance();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedQ = useDebouncedValue(searchTerm, 250);

  const [displayedAthletes, setDisplayedAthletes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const cacheRef = useRef(new Map());
  const observerTarget = useRef(null);

  const fetchPage = useCallback(
    async (q, page, append) => {
      const key = `${q}|${highSchool || ""}|${favoritesOnly ? "fav" : ""}|${page}`;
      const cached = cacheRef.current.get(key);
      if (cached) {
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(cached.items || [])]);
        } else {
          setDisplayedAthletes(cached.items || []);
        }
        setHasMore(!!cached.hasMore);
        setCurrentPage(page);
        return;
      }

      const setBusy = append ? setIsLoadingMore : setLoading;
      setBusy(true);
      try {
        const data = await SearchAthletesPagedAsync(apiInstance, {
          q,
          page,
          pageSize: PAGE_SIZE,
          highSchool,
          favoritesOnly,
        });
        cacheRef.current.set(key, data);
        if (append) {
          setDisplayedAthletes((prev) => [...prev, ...(data.items || [])]);
        } else {
          setDisplayedAthletes(data.items || []);
        }
        setHasMore(!!data.hasMore);
        setCurrentPage(page);
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load athletes";
        toast.error(message, { position: "top-right" });
        if (!append) {
          setDisplayedAthletes([]);
          setHasMore(false);
        }
      } finally {
        setBusy(false);
      }
    },
    // apiInstance is stable: useAxiosInstance() memoizes the axios instance
    // with an empty deps array, so the reference doesn't change between renders.
    [apiInstance, highSchool, favoritesOnly],
  );

  useEffect(() => {
    fetchPage(debouncedQ, 0, false);
  }, [debouncedQ, highSchool, favoritesOnly, fetchPage]);

  // Stash the latest "refresh action" in a ref every render so the refreshKey effect
  // below always invokes the current closure (with the current fetchPage + debouncedQ),
  // never a stale one. This avoids the eslint-disable-line trap and addresses the
  // stale-closure concern from review even though apiInstance is in fact stable.
  const refreshActionRef = useRef(null);
  refreshActionRef.current = () => {
    cacheRef.current.clear();
    fetchPage(debouncedQ, 0, false);
  };

  // When the parent bumps refreshKey (e.g., after a like changes elsewhere), drop the
  // cache and refetch page 0 so stale entries disappear.
  useEffect(() => {
    if (refreshKey === 0) return;
    refreshActionRef.current?.();
  }, [refreshKey]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !loading) {
          fetchPage(debouncedQ, currentPage + 1, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [hasMore, isLoadingMore, loading, currentPage, debouncedQ, fetchPage]);

  // Accepts either a synthetic event (the default from <AthleteSearch>'s <input onChange>)
  // or a raw string value, defensive for any caller that may pass either.
  const handleSearch = (eOrValue) => {
    const value =
      typeof eOrValue === "string" ? eOrValue : (eOrValue?.target?.value ?? "");
    setSearchTerm(value);
  };

  // When an admin edits an athlete's profile via AthleteChip, merge the updated
  // fields into the displayed list in place and invalidate the cache so future
  // page fetches return fresh data.
  const handleChipAthleteUpdate = useCallback((userId, updatedProfile) => {
    setDisplayedAthletes((prev) =>
      prev.map((a) =>
        a.userId === userId
          ? mergeAthleteProfileIntoListItem(a, updatedProfile)
          : a,
      ),
    );
    cacheRef.current.clear();
  }, []);

  if (loading) {
    return (
      <>
        <AthleteSearch handleSearch={handleSearch} />
        <div className="mt-5">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (listVariant === "chips") {
    return (
      <>
        <AthleteSearch handleSearch={handleSearch} />
        <div
          className={`overflow-auto flex flex-col mt-2 pr-4 -mr-4${adjustedHeight ? "" : " flex-1"}`}
          style={adjustedHeight ? { height: `${adjustedHeight}px` } : undefined}
        >
          {displayedAthletes.length > 0 ? (
            <>
              {displayedAthletes.map((athlete) => (
                <AthleteChip
                  key={athlete.userAthleteId || athlete.userId}
                  athlete={athlete}
                  showStarButton
                  {...chipProps}
                  onAthleteUpdate={(updatedProfile) =>
                    handleChipAthleteUpdate(athlete.userId, updatedProfile)
                  }
                />
              ))}
              <div ref={observerTarget} style={{ height: "1px" }} />
              {isLoadingMore && (
                <div className="text-center text-gray-500 text-sm py-3">
                  Loading more athletes...
                </div>
              )}
            </>
          ) : (
            <div className="w-full p-2 mt-5 flex flex-col items-center">
              <img
                alt="No athletes that match your search"
                src="/nodata.gif"
                className="object-cover h-20 w-20 rounded-full border-gray-300 border"
              />
              <span className="mt-4 text-center text-md text-slate-600">
                There are no athletes that match your search
              </span>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <AthleteSearch handleSearch={handleSearch} />
      <ul
        className="list-none p-0 mt-4 overflow-y-auto pr-3 -mr-4"
        style={{ height: `${adjustedHeight}px` }}
      >
        {displayedAthletes.length > 0 ? (
          <>
            {displayedAthletes.map((athlete) => {
              const isSelected = selectedAthlete?.userId === athlete.userId;
              return (
                <li
                  key={athlete.userAthleteId || athlete.userId}
                  className={`cursor-pointer p-2 hover:bg-gray-200 border-slate-500 border mb-4 rounded-md px-2 shrink-0 ${
                    isSelected ? "bg-gray-100" : ""
                  }`}
                  onClick={() => onAthleteSelected && onAthleteSelected(athlete)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex flex-row flex-1">
                      <div className="my-auto rounded-md">
                        <img
                          alt={`${athlete.name} ${athlete.lastname}`}
                          src={athlete.profilePicture || "/default-profile-img.png"}
                          className="grow-0 shrink-0 rounded-md h-[40px] w-[40px]"
                          height={40}
                          width={40}
                        />
                      </div>
                      <div className="ml-4">
                        <p className="text-base lg:text-lg uppercase">
                          {athlete.name} {athlete.lastname}
                        </p>
                        <p className="text-sm text-gray-700">{athlete.position}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-center -mt-1">
                        <CircularProgressBar
                          progress={athlete.positionScore || 0}
                          size={40}
                          strokeWidth={4}
                        />
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </div>
                </li>
              );
            })}

            <div ref={observerTarget} style={{ height: "1px" }} />

            {isLoadingMore && (
              <li className="flex justify-center py-4">
                <div className="text-gray-500 text-sm">Loading more athletes...</div>
              </li>
            )}

            {!hasMore && displayedAthletes.length > 0 && (
              <li className="flex justify-center py-4">
                <div className="text-gray-400 text-sm">
                  All athletes loaded ({displayedAthletes.length} total)
                </div>
              </li>
            )}
          </>
        ) : (
          <div className="w-full p-2 mt-5 flex flex-col items-center">
            <img
              alt="No athletes that match your search"
              src="/nodata.gif"
              className="object-cover h-20 w-20 rounded-full border-gray-300 border"
            />
            <span className="mt-4 text-center text-md text-slate-600">
              There are no athletes that match your search
            </span>
          </div>
        )}
      </ul>
    </>
  );
}
